import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { pwaApi } from '../../lib/api.js';
import { useSessionStore } from '../../store/sessionStore.js';
import StatePicker from '../../components/StatePicker.js';

const SELECT_CLASS = 'bg-white text-gray-900 px-3 py-2.5 rounded text-base disabled:opacity-50 w-full';

interface StateLogRow {
  id: number;
  machineStateId: number;
  note: string | null;
  occurredAt: string;
  machineState: { id: number; valueIt: string; color: string; subcategory: { id: number; category: { id: number; valueIt: string } } };
}

const DATETIME_LOCAL_FORMAT = "yyyy-MM-dd'T'HH:mm";

export default function StateHistoryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sessionId, availableStates, plantTimezone, refreshProductionSummary } = useSessionStore();
  const [rows, setRows] = useState<StateLogRow[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [edited, setEdited] = useState<Record<number, { machineStateId?: number; note?: string; occurredAt?: string }>>({});
  const [loading, setLoading] = useState(true);

  // Riga nuova: lo stato va scelto sui 3 livelli, non ereditato d'ufficio dal primo della lista
  const [addOpen, setAddOpen] = useState(false);
  const [newStateId, setNewStateId] = useState<number | null>(null);
  const [newNote, setNewNote] = useState('');
  const [newOccurredAt, setNewOccurredAt] = useState('');

  async function load() {
    if (!sessionId) return;
    setLoading(true);
    const { data } = await pwaApi.get(`/tablet/sessions/${sessionId}/state-logs`);
    setRows(data.data as StateLogRow[]);
    setLoading(false);
  }

  useEffect(() => { load(); }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggleSelect(id: number) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function setEdit(id: number, patch: { machineStateId?: number; note?: string; occurredAt?: string }) {
    setEdited(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  }

  function openAddRow() {
    setNewStateId(null);
    setNewNote('');
    setNewOccurredAt(formatInTimeZone(new Date(), plantTimezone, DATETIME_LOCAL_FORMAT));
    setAddOpen(true);
  }

  async function handleAddRow() {
    if (!sessionId || !newStateId) return;
    await pwaApi.post(`/tablet/sessions/${sessionId}/state-logs`, {
      machineStateId: newStateId,
      note: newNote.trim() || undefined,
      occurredAt: newOccurredAt ? fromZonedTime(newOccurredAt, plantTimezone).toISOString() : undefined,
    });
    setAddOpen(false);
    await load();
  }

  async function handleSaveSelected() {
    if (!sessionId) return;
    const ids = Array.from(selected).filter(id => edited[id]);
    await Promise.all(ids.map(id => {
      const patch = edited[id]!;
      const body: Record<string, unknown> = {};
      if (patch.machineStateId !== undefined) body['machineStateId'] = patch.machineStateId;
      if (patch.note !== undefined) body['note'] = patch.note;
      if (patch.occurredAt !== undefined) body['occurredAt'] = fromZonedTime(patch.occurredAt, plantTimezone).toISOString();
      return pwaApi.put(`/tablet/sessions/${sessionId}/state-logs/${id}`, body);
    }));
    setEdited({});
    await load();
    await refreshProductionSummary();
  }

  async function handleDeleteSelected() {
    if (!sessionId || selected.size === 0) return;
    await Promise.all(Array.from(selected).map(id => pwaApi.delete(`/tablet/sessions/${sessionId}/state-logs/${id}`)));
    setSelected(new Set());
    await load();
  }

  return (
    <div className="h-full overflow-y-auto" style={{ background: '#1a1a2e' }}>
      <div className="flex items-center gap-4 px-4 h-16 bg-gray-900 border-b border-gray-700">
        <button onClick={() => navigate('/workstation')} className="bg-gray-700 hover:bg-gray-600 active:bg-gray-500 text-white px-5 py-3 rounded-lg font-bold text-sm">
          ← {t('history.close')}
        </button>
        <div className="text-white font-bold">{t('history.states.title')}</div>
      </div>

      <div className="p-4 flex items-center gap-3">
        <button onClick={openAddRow} disabled={availableStates.length === 0} className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white px-5 py-3 rounded-lg font-bold text-base">
          {t('history.new')}
        </button>
        <button onClick={handleSaveSelected} disabled={Object.keys(edited).length === 0} className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white px-5 py-3 rounded-lg font-bold text-base">
          {t('history.saveSelected')}
        </button>
        <button onClick={handleDeleteSelected} disabled={selected.size === 0} className="bg-red-700 hover:bg-red-600 disabled:bg-gray-600 text-white px-5 py-3 rounded-lg font-bold text-base">
          {t('history.deleteOne')}
        </button>
      </div>

      {addOpen && (
        <div className="mx-4 mb-4 bg-gray-900 border border-gray-700 rounded-lg p-4 space-y-3">
          <StatePicker
            states={availableStates}
            value={newStateId}
            onChange={setNewStateId}
            selectClassName="bg-gray-800 text-white px-3 py-2 rounded border border-gray-600 disabled:opacity-50 w-full"
            stacked={false}
          />
          <div className="flex flex-wrap gap-3">
            <input
              type="text"
              className="flex-1 min-w-[12rem] bg-gray-800 text-white px-3 py-2 rounded border border-gray-600"
              placeholder={t('history.note')}
              value={newNote}
              onChange={e => setNewNote(e.target.value)}
            />
            <input
              type="datetime-local"
              className="bg-gray-800 text-white px-3 py-2 rounded border border-gray-600"
              value={newOccurredAt}
              onChange={e => setNewOccurredAt(e.target.value)}
            />
            <button onClick={() => setAddOpen(false)} className="bg-gray-700 hover:bg-gray-600 text-white px-5 py-3 rounded-lg font-bold text-base">
              {t('fermo.cancel')}
            </button>
            <button onClick={handleAddRow} disabled={!newStateId} className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white px-5 py-3 rounded-lg font-bold text-base">
              {t('fermo.confirm')}
            </button>
          </div>
        </div>
      )}

      <div className="px-4 overflow-x-auto">
        <table className="w-full border-collapse min-w-[900px]">
          <thead>
            <tr className="bg-gray-800 text-gray-300 text-sm">
              <th className="p-2 w-14"></th>
              <th className="p-2 text-left">{t('history.state')}</th>
              <th className="p-2 text-left">{t('history.note')}</th>
              <th className="p-2 text-left">{t('history.time')} ({plantTimezone})</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const patch = edited[row.id];
              const localDateTime = formatInTimeZone(new Date(row.occurredAt), plantTimezone, DATETIME_LOCAL_FORMAT);
              return (
                <tr key={row.id} className="border-b border-gray-800">
                  <td className="p-2 text-center">
                    <input type="checkbox" className="w-6 h-6 accent-blue-500" checked={selected.has(row.id)} onChange={() => toggleSelect(row.id)} />
                  </td>
                  <td className="p-2 min-w-[24rem]">
                    <StatePicker
                      states={availableStates}
                      value={patch?.machineStateId ?? row.machineStateId}
                      onChange={id => { if (id !== null) setEdit(row.id, { machineStateId: id }); }}
                      selectClassName={SELECT_CLASS}
                      stacked={false}
                    />
                  </td>
                  <td className="p-2">
                    <input
                      type="text"
                      className="bg-white text-gray-900 px-3 py-2.5 rounded text-base w-full"
                      value={patch?.note ?? row.note ?? ''}
                      onChange={e => setEdit(row.id, { note: e.target.value })}
                    />
                  </td>
                  <td className="p-2">
                    <input
                      type="datetime-local"
                      className="bg-white text-gray-900 px-3 py-2.5 rounded text-base"
                      value={patch?.occurredAt ?? localDateTime}
                      onChange={e => setEdit(row.id, { occurredAt: e.target.value })}
                    />
                  </td>
                </tr>
              );
            })}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={4} className="p-4 text-center text-gray-500 text-sm">{t('history.states.empty')}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
